import {
  DRAFT_EDITION, clearRun, clearTask, countsFor, latchEffects, recordAttempt, recordVisit, rollFor, settleArrival,
  startSession, visitCount, visitsOf,
  type AttemptCounts, type AttemptRecord, type FlowEffect, type SessionRecord,
} from '@agavi/blocks';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * The run a teacher is previewing, held ABOVE the router.
 *
 * It has to live here, and the reason is not task-to-task navigation. `/task/:id` keeps the same
 * component across a task change, so state inside the workspace would survive one by accident --
 * but Preview launched from the experience builder crosses `/experience/:id` to `/task/:id`,
 * which is a different route and a different component. No amount of same-component luck covers
 * that, so the session sits above `<Routes>` where neither case can touch it.
 *
 * ## It is discarded, and that is the point
 *
 * A teacher previewing a lesson forty times must not leave forty sessions in their own data. So
 * this backing is memory only: no fetch, no write, gone when the preview ends. The STUDENT's
 * session is the same `SessionRecord` with a persistent backing -- same shape, same derivations
 * from `@agavi/blocks`, different lifetime. Keeping the model in the package rather than in here
 * is what stops the two drifting.
 *
 * ## Why a ref as well as state
 *
 * Recording an attempt happens inside a submit handler that then immediately asks for the counts
 * to resolve flow with. React state is a snapshot for the render it was read in, so reading it
 * back in the same tick would see the session as it was BEFORE the attempt -- an off-by-one that
 * would route on stale counts. The ref is the live value; the state exists to re-render.
 */

/** An attempt that has been graded but not yet recorded, because its outcome is still being
 *  resolved. Exactly the fields a tally reads. */
export type PendingAttempt = Pick<AttemptRecord, 'results' | 'signatures'>;

interface PreviewSession {
  /** The run in progress, or null when the teacher is simply editing. */
  session: SessionRecord | null;
  /**
   * Begins a run. Any run already in progress is abandoned -- there is only ever one.
   *
   * `seedRolls` carries variable values forward into the new run, which is what separates the two
   * ways of starting over. Reset is "same variant, another go" and passes them; Reroll is "another
   * student's variant" and does not. Both drop the attempt log, because both are a fresh run --
   * the session has exactly two end triggers and this is one of them.
   */
  start: (experienceId: string, seedRolls?: SessionRecord['rolls']) => void;
  /** Ends it and throws the record away. */
  end: () => void;
  /** Appends a submission and returns the session as it now is, for the same tick. */
  record: (attempt: AttemptRecord) => SessionRecord;
  /** This task's variable values, rolled on first ask and sticky for the whole run. */
  roll: (taskId: string, produce: () => Record<string, string>) => Record<string, string>;
  /** Latch this submission's effects. They stay on for the rest of the run -- see `latchEffects`. */
  applyEffects: (effects: FlowEffect[]) => void;
  /**
   * Apply this visit's arrival policy: drop the named blocks' latched effects, ONCE.
   *
   * The caller passes the blocks to reset because the policy is per block and lives on the
   * task's flow, which the session has never known about. Safe to call on every render -- see
   * `settleArrival`, which refuses a visit it has already settled.
   */
  settle: (taskId: string, resetBlockIds: readonly string[]) => void;
  /**
   * Every tally Flow can ask about, for one task.
   *
   * `pending` is the submission being resolved right now, which counts toward its own conditions
   * -- see `countsFor`. Omit it to ask what the log alone says.
   */
  counts: (taskId: string, pending?: PendingAttempt) => AttemptCounts | null;
  /** One entry per visit, each the attempt it ended on -- what review flips through. */
  visits: (taskId: string) => AttemptRecord[];
  /**
   * How many times they have ARRIVED at this task, the one they are on included.
   *
   * Beside `visits` rather than derived from it, because the two answer different questions and
   * only one of them can answer this. `visits` flips through what was LEFT BEHIND, so a task
   * with nothing to answer has no entries however often it is walked through; this counts the
   * arrivals themselves, which is what a `visits` condition and a reset-on-return need.
   */
  visitCount: (taskId: string) => number;
  /**
   * THE NEXT ARRIVAL AT THIS TASK IS A FLOW ARRIVAL, not a teacher walking the board.
   *
   * Two ways to reach a task in Preview and they mean opposite things. The FLOW moving a
   * student on is the student arriving: it ends the previous visit, so the arrival policy runs
   * and the task opens for a fresh attempt. A teacher stepping back through the stepper is
   * INSPECTION — they want to see what the run did, not start it again — and treating that as
   * an arrival wiped the very answers they came back to look at.
   *
   * Called immediately before navigating, and consumed by the first arrival at that task. A
   * one-shot rather than a mode, because the distinction belongs to a single navigation and
   * anything longer-lived would survive into the next one.
   */
  arriveByFlow: (taskId: string) => void;
  /**
   * UNDO ONE TASK'S HISTORY: make it a task the run has never reached.
   *
   * Replaces a forget-the-arrivals helper that dropped the visits and left the attempts, which
   * nothing ever called -- just as well, because a task with two recorded attempts and no visit
   * to hold them is a state the runtime cannot produce. See `clearTask`: the attempts go too,
   * the answers go with them because they live inside them, and `blockIds` names the blocks
   * whose latched effects must go as well.
   */
  clearTask: (taskId: string, blockIds: readonly string[]) => void;
  /** The same for every task at once -- the run as it was when Preview opened. */
  clearRun: () => void;
  /**
   * ANSWERS STOOD IN FOR A REFERENCE NOBODY HAS REACHED YET, keyed by alias.
   *
   * A teacher testing a sentence that quotes an earlier answer has two ways to fill it: walk the
   * experience and answer the source for real, or say what it would have been. This is the
   * second. The first needs nothing -- the session's own attempts already resolve it.
   *
   * DELIBERATELY NOT AN ATTEMPT. Writing one would make the source task answered: flow
   * conditions would fire on it, scoring would count it, and the review log would show a visit
   * the student never made. A stand-in is a testing aid for the sentence, so it is held beside
   * the run rather than inside it and nothing but the reference renderer ever reads it.
   *
   * A real answer always wins. This is consulted only where the run resolved nothing, so
   * walking the source later replaces the stand-in without clearing it -- and going back to a
   * path that does not reach the source brings it back.
   */
  standIns: Record<string, unknown>;
  /** Set one, or clear it with `null` -- which puts the dimmed placeholder back. */
  setStandIn: (aliasId: string, answer: unknown | null) => void;
}

const PreviewSessionContext = createContext<PreviewSession | null>(null);

export function PreviewSessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<SessionRecord | null>(null);
  const location = useLocation();
  const live = useRef<SessionRecord | null>(null);
  /** See `arriveByFlow`. A ref, not state: it is consumed by an effect and must not re-render. */
  const pendingFlowArrival = useRef<string | null>(null);

  const commit = useCallback((next: SessionRecord | null) => {
    live.current = next;
    setSession(next);
    return next;
  }, []);

  const start = useCallback((experienceId: string, seedRolls?: SessionRecord['rolls']) => {
    // DRAFT_EDITION, not the live edition number: Preview runs what the teacher is editing, which
    // is by definition not any published edition. Claiming the live one would make a preview
    // record indistinguishable from a student's while describing content nobody was served.
    const fresh = startSession(experienceId, DRAFT_EDITION, `prv_${crypto.randomUUID()}`);
    commit(seedRolls ? { ...fresh, rolls: seedRolls } : fresh);
    setStandIns({});
  }, [commit]);

  /*
   * Beside the run, not inside it, and so held beside the record too — see `standIns`.
   *
   * Dropped whenever the run is, which is both of the session's end triggers: a stand-in
   * describes one walk through one experience, and carrying it into the next would quietly
   * answer a reference in a run nobody set it for.
   */
  const [standIns, setStandIns] = useState<Record<string, unknown>>({});

  const end = useCallback(() => { commit(null); setStandIns({}); }, [commit]);

  const arriveByFlow = useCallback((taskId: string) => { pendingFlowArrival.current = taskId; }, []);

  /**
   * A run lives on the task surface and nowhere else.
   *
   * Exit is not the only way out: the breadcrumbs, the back button and the board all leave too,
   * and a run that outlived them stayed live with nothing showing it. The teacher would then open
   * the next task from the builder and land in a student view they never asked for, with no Edit
   * in sight -- which is exactly what happens today.
   *
   * Keyed on the ROUTE rather than on this page unmounting, for two reasons. Routing between
   * tasks deliberately keeps the task page mounted, so unmount is not a reliable "left Preview".
   * And StrictMode double-invokes effects in development, so an unmount cleanup would end the run
   * a moment after the builder started it.
   */
  useEffect(() => {
    if (live.current && !location.pathname.startsWith('/app/task/')) { commit(null); setStandIns({}); }
  }, [location.pathname, commit]);

  /**
   * ARRIVALS ARE LOGGED HERE, from the navigation, not from the task page mounting.
   *
   * The task page is the obvious home for this and the wrong one. Routing between tasks keeps it
   * mounted, so it would miss the arrivals that matter most; StrictMode mounts it twice, so it
   * would double-count the rest; and a refresh remounts it, which for a student -- whose session
   * is persisted -- would invent a visit they never made. The route changing IS the arrival, and
   * this is where the route changes.
   *
   * `session?.id` is in the deps for the case the pathname cannot see: Preview started FROM a
   * task, where the run appears without the address moving. A new run means a new first arrival
   * at wherever they already are, which is also why Reset and Reroll land correctly here.
   *
   * `recordVisit` refuses a repeat of the task already at the end of the log, so everything that
   * re-fires this effect without actually moving anyone -- a query string changing, a remount
   * after a refresh -- costs nothing.
   */
  useEffect(() => {
    const current = live.current;
    const id = location.pathname.match(/^\/app\/task\/([^/]+)/)?.[1];
    if (!current || !id) return;
    /*
     * ONLY A FLOW ARRIVAL COUNTS — see `arriveByFlow`.
     *
     * The first arrival of a run counts too, and is the one case that needs saying: nothing
     * routed the teacher there, but a run with no arrival at all has no visit window, so
     * `lastAttemptFor(…, 'visit')` would read the whole session and the arrival policy would
     * never settle.
     *
     * Everything else — the stepper, a breadcrumb, the board — leaves the log alone. That is
     * what keeps a completed task completed when you step back to look at it: the previous
     * visit is still the current one, its attempt is still in scope, and the task stays locked
     * with the answers that earned it.
     */
    const byFlow = pendingFlowArrival.current === id;
    if (byFlow) pendingFlowArrival.current = null;
    if (!byFlow && (current.visits?.length ?? 0) > 0) return;

    const next = recordVisit(current, id);
    if (next !== current) commit(next);
  }, [location.pathname, session?.id, commit]);

  const record = useCallback((attempt: AttemptRecord) => {
    const current = live.current;
    if (!current) throw new Error('recordAttempt with no preview session in progress');
    return commit(recordAttempt(current, attempt))!;
  }, [commit]);

  const roll = useCallback((taskId: string, produce: () => Record<string, string>) => {
    const current = live.current;
    // Outside a preview there is nothing sticky to hold a roll, so the caller gets a fresh one
    // and nothing is remembered -- which is exactly the block-test behaviour.
    if (!current) return produce();
    const next = rollFor(current, taskId, produce);
    if (next.session !== current) commit(next.session);
    return next.roll;
  }, [commit]);

  const applyEffects = useCallback((effects: FlowEffect[]) => {
    const current = live.current;
    if (!current) return;
    const next = latchEffects(current, effects);
    if (next !== current) commit(next);
  }, [commit]);

  const settle = useCallback((taskId: string, resetBlockIds: readonly string[]) => {
    const current = live.current;
    if (!current) return;
    const next = settleArrival(current, taskId, resetBlockIds);
    if (next !== current) commit(next);
  }, [commit]);

  const counts = useCallback((taskId: string, pending?: PendingAttempt) =>
    (live.current ? countsFor(live.current, taskId, pending) : null), []);

  const visits = useCallback((taskId: string) =>
    (live.current ? visitsOf(live.current, taskId) : []), []);

  const visitCountOf = useCallback((taskId: string) =>
    (live.current ? visitCount(live.current, taskId) : 0), []);

  const clearTaskHistory = useCallback((taskId: string, blockIds: readonly string[]) => {
    const current = live.current;
    if (!current) return;
    commit(clearTask(current, taskId, blockIds));
  }, [commit]);

  const clearRunHistory = useCallback(() => {
    const current = live.current;
    if (!current) return;
    commit(clearRun(current));
  }, [commit]);

  const setStandIn = useCallback((aliasId: string, answer: unknown | null) => {
    setStandIns(prev => {
      if (answer === null) {
        if (!(aliasId in prev)) return prev;
        const { [aliasId]: _gone, ...rest } = prev;
        return rest;
      }
      return { ...prev, [aliasId]: answer };
    });
  }, []);

  const value = useMemo<PreviewSession>(
    () => ({
      session, start, end, record, roll, applyEffects, settle, counts,
      visits, visitCount: visitCountOf, clearTask: clearTaskHistory, clearRun: clearRunHistory,
      arriveByFlow, standIns, setStandIn,
    }),
    [session, start, end, record, roll, applyEffects, settle, counts,
      visits, visitCountOf, clearTaskHistory, clearRunHistory, arriveByFlow, standIns, setStandIn],
  );

  return <PreviewSessionContext.Provider value={value}>{children}</PreviewSessionContext.Provider>;
}

/**
 * The settings a Change has turned on so far, or none.
 *
 * Deliberately TOLERANT of there being no provider, unlike `usePreviewSession`. The strict hook is
 * right for code that cannot work without a run -- grading a submission into one. Rendering a task
 * is not that: the editor's canvas shows the same blocks with no run in sight, and a component
 * that works perfectly well without a session should not throw asking for one.
 */
export function useLatchedEffects(): Record<string, FlowEffect> {
  return useContext(PreviewSessionContext)?.session?.effects ?? NO_EFFECTS;
}

/** One stable empty object, so "no run" never looks like a new value to React. */
const NO_EFFECTS: Record<string, FlowEffect> = {};

/** The run in progress, or null. Tolerant of there being no provider, unlike `usePreviewSession`. */
/**
 * The stand-ins for this run, and the way to set one — or nothing, outside a run.
 *
 * TOLERANT of there being no provider, like `useLatchedEffects` and for the same reason: the
 * reference renderer runs on the editor's canvas too, where there is no run and no stand-in to
 * find. A caller gets an empty map and a setter that is absent, which is also what tells the
 * renderer not to offer the control.
 */
export function useRecallStandIns(): { standIns: Record<string, unknown>; set?: PreviewSession['setStandIn'] } {
  const ctx = useContext(PreviewSessionContext);
  return ctx?.session
    ? { standIns: ctx.standIns, set: ctx.setStandIn }
    : NO_STAND_INS;
}

/** One frozen empty map, so a caller outside a run gets a stable identity to depend on. */
const NO_STAND_INS: { standIns: Record<string, unknown>; set?: PreviewSession['setStandIn'] } =
  { standIns: {} };

export function useRunSession(): SessionRecord | null {
  return useContext(PreviewSessionContext)?.session ?? null;
}

export function usePreviewSession(): PreviewSession {
  const ctx = useContext(PreviewSessionContext);
  if (!ctx) throw new Error('usePreviewSession must be used inside PreviewSessionProvider');
  return ctx;
}
